'use client';

import { useState } from 'react';
import { BubbleRows } from '@/components/chat/BubbleBlock';
import { asOfLabel, type RevenueChartBlock } from '@/lib/chat/blocks/revenue-chart';

/**
 * Up to six months as stacked bars, paid under still unpaid, the current month
 * hatched and labelled "so far". Tapping a month shows what it invoiced and
 * what is still unpaid. Board: `project/Month.dc.html`.
 *
 * 🚨 Nothing here computes a figure. Every amount is off the block, built on
 * the server from the same rows and arithmetic as the reply's own sentence
 * (`revenue-chart.ts`). The only arithmetic here is bar HEIGHT.
 *
 * No tap here asks Mucka anything or changes anything: picking a month is a
 * view, and Open Revenue goes to a page. So nothing is turned off when `live`
 * is false, the same as the owed table's rows and the week's stops; a stored
 * reply says which day its "still unpaid" was read on instead.
 */
export interface RevenueChartProps {
  block: RevenueChartBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}

function pounds(n: number): string {
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Percentage of the bar area, with a sliver kept for any month that has money. */
function heightPct(value: number, max: number): number {
  if (!(value > 0) || !(max > 0)) return 0;
  return Math.max(1.5, (value / max) * 100);
}

export function RevenueChart({ block, onOpen }: RevenueChartProps) {
  const [picked, setPicked] = useState(block.focusMonth);
  const shown = block.months.find(m => m.month === picked) ?? block.months[block.months.length - 1];
  const max = Math.max(0, ...block.months.map(m => m.invoiced));
  const nothing = max === 0;
  const hasCurrent = block.months.some(m => m.current);
  const first = block.months[0];
  const last = block.months[block.months.length - 1];

  return (
    <div className="mchat-rev">
      {nothing ? (
        <p className="mchat-rev-empty">
          Nothing invoiced from {first.label} to {last.label}{block.oneClient ? ' for this customer' : ''}. Drafts are not counted until they are sent.
        </p>
      ) : (
        <>
          <div className="mchat-rev-bars" role="group" aria-label={`Invoiced by month, ${first.label} to ${last.label}`}>
            {block.months.map(m => {
              const on = m.month === shown.month;
              const totalPct = heightPct(m.invoiced, max);
              const paidShare = m.invoiced > 0 ? (m.paid / m.invoiced) * 100 : 0;
              return (
                <button
                  key={m.month}
                  type="button"
                  className={`mchat-rev-bar${on ? ' mchat-rev-bar--on' : ''}`}
                  aria-pressed={on}
                  aria-label={`${m.name}, ${pounds(m.invoiced)} invoiced, ${pounds(m.unpaid)} still unpaid`}
                  onClick={() => setPicked(m.month)}
                >
                  <span className="mchat-rev-stack" style={{ height: `${totalPct}%` }}>
                    <span
                      className={`mchat-rev-unpaid${m.current ? ' mchat-rev-unpaid--sofar' : ''}`}
                      style={{ height: `${100 - paidShare}%` }}
                    />
                    <span className="mchat-rev-paid" style={{ height: `${paidShare}%` }} />
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mchat-rev-labels" aria-hidden="true">
            {block.months.map(m => (
              <span key={m.month} className={`mchat-rev-label${m.month === shown.month ? ' mchat-rev-label--on' : ''}`}>
                {m.label}
                {m.current && <span className="mchat-rev-sofar">so far</span>}
              </span>
            ))}
          </div>
          <div className="mchat-rev-legend">
            <span className="mchat-rev-key"><span className="mchat-rev-swatch mchat-rev-swatch--paid" />Paid</span>
            <span className="mchat-rev-key"><span className="mchat-rev-swatch mchat-rev-swatch--unpaid" />Unpaid</span>
            {hasCurrent && <span className="mchat-rev-key"><span className="mchat-rev-swatch mchat-rev-swatch--sofar" />Month so far</span>}
          </div>

          <div className="mchat-rev-detail" aria-live="polite">
            <span className="mchat-rev-detail-title">{shown.name}</span>
            <span className="mchat-rev-figs">
              <span className="mchat-rev-fig">
                <span className="mchat-rev-fig-num">{pounds(shown.invoiced)}</span>
                <span className="mchat-rev-fig-cap">invoiced{shown.count ? ` · ${shown.count} invoice${shown.count === 1 ? '' : 's'}` : ''}</span>
              </span>
              <span className="mchat-rev-fig">
                <span className={`mchat-rev-fig-num${shown.unpaidHigh ? ' mchat-rev-fig-num--high' : ''}`}>{pounds(shown.unpaid)}</span>
                <span className="mchat-rev-fig-cap">still unpaid{shown.unpaidHigh ? ' · over a quarter' : ''}</span>
              </span>
            </span>
          </div>
        </>
      )}

      <p className="mchat-rev-note">
        {[
          block.oneClient ? 'This customer only' : null,
          `Unpaid as at ${asOfLabel(block.asOf)}`,
          block.hiddenMonths
            ? `${block.hiddenMonths} earlier month${block.hiddenMonths === 1 ? '' : 's'} in the total, not drawn`
            : null,
        ].filter(Boolean).join(' · ')}
      </p>

      {block.revenuePath && (
        <BubbleRows
          label="Revenue"
          rows={[{ id: 'open-revenue', title: 'Open Revenue', kind: 'go', onPress: () => onOpen(block.revenuePath!) }]}
        />
      )}
    </div>
  );
}
